<script setup lang="ts">
import SvgIcon from "~/components/Common/SvgIcon.vue";
import { copilotCanSend } from "~/utils/helpers/copilot/send-intent";

const props = withDefaults(
  defineProps<{
    modelValue: string;
    placeholder?: string;
    /** Hard lock of the composer. `busy` alone keeps the textarea live so the caret is never lost. */
    disabled?: boolean;
    /** A response is streaming: send turns into stop. */
    busy?: boolean;
    canAttach?: boolean;
    accept?: string;
    /** Staged file waiting to be sent. Shown as a removable chip; null when nothing is attached. */
    attachmentName?: string | null;
  }>(),
  {
    placeholder: "Écrivez un message...",
    disabled: false,
    busy: false,
    canAttach: false,
    accept: "application/pdf",
    attachmentName: null,
  },
);

const emit = defineEmits<{
  "update:modelValue": [value: string];
  send: [];
  stop: [];
  file: [file: File];
  "remove-attachment": [];
}>();

const textareaRef = ref<HTMLTextAreaElement | null>(null);
const fileInputRef = ref<HTMLInputElement | null>(null);
const MAX_HEIGHT_PX = 128;

const canSend = computed(() =>
  copilotCanSend(props.modelValue, props.attachmentName, { disabled: props.disabled, busy: props.busy }),
);

/** Every action hands the caret back to the composer, so the next message can be typed right away. */
function focusTextarea() {
  textareaRef.value?.focus();
}

/** Grows the textarea up to MAX_HEIGHT_PX, then lets scrolling take over. */
function adjustHeight() {
  const textarea = textareaRef.value;
  if (!textarea) return;

  textarea.style.height = "auto";
  textarea.style.height = `${Math.min(textarea.scrollHeight, MAX_HEIGHT_PX)}px`;
}

function onInput(event: Event) {
  emit("update:modelValue", (event.target as HTMLTextAreaElement).value);
}

function onSend() {
  if (!canSend.value) return;

  emit("send");
  focusTextarea();
}

function onStop() {
  emit("stop");
  focusTextarea();
}

function openFilePicker() {
  if (props.disabled || props.busy) return;

  fileInputRef.value?.click();
}

function onFileSelected(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];

  if (file) emit("file", file);
  input.value = "";
  focusTextarea();
}

watch(
  () => props.modelValue,
  () => nextTick(adjustHeight),
);

onMounted(adjustHeight);

defineExpose({ focus: focusTextarea });
</script>

<template>
  <div
    class="flex w-full flex-col gap-[10px] rounded-xl border border-stroke-color bg-lightest-grey-color p-3 transition-colors"
    :class="disabled ? null : 'focus-within:border-primary-blue-color'"
  >
    <div
      v-if="attachmentName"
      class="flex min-w-0 max-w-full items-center gap-2 self-start rounded-lg border border-stroke-color bg-light-color px-2 py-1"
    >
      <SvgIcon name="attached-document" class="h-[14px] w-[14px] shrink-0 text-primary-blue-color" />
      <span class="meta min-w-0 truncate text-primary-color" :title="attachmentName">{{ attachmentName }}</span>
      <button
        type="button"
        class="shrink-0 text-secondary-color transition-colors hover:text-primary-color disabled:cursor-not-allowed disabled:text-stroke-grey-color"
        :disabled="disabled || busy"
        title="Retirer le document"
        aria-label="Retirer le document"
        @click="emit('remove-attachment')"
      >
        <SvgIcon name="close" class="h-[12px] w-[12px]" />
      </button>
    </div>

    <div class="px-1 pt-[2px]">
      <textarea
        ref="textareaRef"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        rows="1"
        class="body_regular block max-h-32 min-h-[21px] w-full resize-none bg-transparent leading-[21px] text-primary-color outline-none placeholder:text-grey-medium-color disabled:cursor-not-allowed"
        @input="onInput"
        @keydown.enter.exact.prevent="onSend"
      ></textarea>
    </div>

    <div class="flex items-center justify-between">
      <!-- Composer-owned controls: the design system has no filled icon-only button. -->
      <button
        v-if="canAttach"
        type="button"
        class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-secondary-color transition-colors hover:bg-lightest-light-grey-color disabled:cursor-not-allowed disabled:text-stroke-grey-color disabled:hover:bg-transparent"
        :disabled="disabled || busy"
        title="Joindre un document"
        aria-label="Joindre un document"
        @click="openFilePicker()"
      >
        <SvgIcon name="attached-document" class="h-[16px] w-[16px]" />
      </button>

      <button
        v-if="busy"
        type="button"
        class="ml-auto flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary-color text-light-color transition-colors hover:bg-primary-color"
        title="Arrêter la génération"
        aria-label="Arrêter la génération"
        @click="onStop()"
      >
        <SvgIcon name="stop" class="h-[18px] w-[18px]" />
      </button>
      <button
        v-else
        type="button"
        class="ml-auto flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-light-color transition-colors"
        :class="canSend ? 'bg-primary-blue-color hover:bg-blue-dark' : 'cursor-not-allowed bg-stroke-grey-color'"
        :disabled="!canSend"
        title="Envoyer"
        aria-label="Envoyer"
        @click="onSend()"
      >
        <SvgIcon name="arrow_top" class="h-[18px] w-[18px]" />
      </button>
    </div>

    <input v-if="canAttach" ref="fileInputRef" type="file" :accept="accept" class="hidden" @change="onFileSelected" />
  </div>
</template>
